import React, { useContext, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import * as Yup from "yup";
import Input from "../../../../components/form/input";
import { formatErrors, handleSubmit } from "../../../../services/utils/processors";
import { ToastContext } from "../../../../services/contexts/toast-context";
import { FormWrapper, FormSection, GridRow, AccordionHeader, AddButtonRow, ActionRow } from "../form.styles";

const Certifications = ({ dbData = null, loading = false, onSave, onBack }) => {
  const toastRef = useContext(ToastContext);
  const [certifications, setCertifications] = useState([]);
  const [initialCertifications, setInitialCertifications] = useState([]);
  const [certificationError, setCertificationError] = useState({});
  const baselineKey = JSON.stringify(dbData || []);

  const schema = Yup.array().of(
    Yup.object({
      title: Yup.string().required("Certification Title is required"),
      issuer: Yup.string().nullable().notRequired(),
      credential_id: Yup.string().nullable().notRequired(),
      credential_url: Yup.string().url("Invalid URL").nullable().notRequired(),
    })
  );

  useEffect(() => {
    const baseline = Array.isArray(dbData) ? dbData : [];
    setCertifications(baseline);
    setInitialCertifications(JSON.parse(JSON.stringify(baseline)));
    setCertificationError({});
  }, [baselineKey]);

  const normalize = (items) => items.map((item) => ({
    title: item.title?.trim() || '',
    issuer: item.issuer?.trim() || '',
    credential_id: item.credential_id?.trim() || '',
    credential_url: item.credential_url?.trim() || '',
  })).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  const isDirty = JSON.stringify(normalize(certifications)) !== JSON.stringify(normalize(initialCertifications));

  const handleInputChange = (value, index, field) => {
    const updated = [...certifications];
    updated[index] = { ...updated[index], [field]: value };
    setCertifications(updated);
  };

  const addCertification = () => {
    setCertifications([...certifications, { title: "", issuer: "", credential_id: "", credential_url: "" }]);
  };

  const removeCertification = (index) => {
    setCertifications(certifications.filter((_, i) => i !== index));
  };

  const handleSubmitData = () => {
    handleSubmit(certifications, schema, setCertificationError, onSave);
    if (certificationError && Object.entries(certificationError).length !== 0) {
      toastRef.current.show({ severity: "error", summary: "Error", detail: formatErrors(certificationError) });
    }
    setCertificationError({});
  };

  return (
    <FormWrapper>
      <AddButtonRow>
        <Button type="button" label="Add Certification" icon="pi pi-plus" className="p-button-success" onClick={addCertification} />
      </AddButtonRow>

      <Accordion style={{ width: "100%" }}>
        {certifications.map((cert, index) => (
          <AccordionTab key={index}
            header={
              <AccordionHeader>
                <span>{cert.title || `Certification ${index + 1}`}</span>
                <Button type="button" icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text"
                  onClick={(event) => { event.stopPropagation(); removeCertification(index); }} tooltip="Remove" tooltipOptions={{ position: "top" }} />
              </AccordionHeader>
            }
            style={{ minWidth: "50vw", backgroundColor: "transparent", border: "none", marginBottom: 16 }}
          >
            <FormSection>
              <GridRow>
                <Input value={cert.title} label="Title" width="100%"
                  onChange={(v) => handleInputChange(v, index, "title")} error={certificationError?.[index]?.title} />
                <Input value={cert.issuer} label="Issuer" width="100%"
                  onChange={(v) => handleInputChange(v, index, "issuer")} error={certificationError?.[index]?.issuer} />
              </GridRow>
              <GridRow>
                <Input value={cert.credential_id} label="Credential ID" width="100%"
                  onChange={(v) => handleInputChange(v, index, "credential_id")} error={certificationError?.[index]?.credential_id} />
                <Input value={cert.credential_url} label="Credential URL" width="100%"
                  onChange={(v) => handleInputChange(v, index, "credential_url")} error={certificationError?.[index]?.credential_url} />
              </GridRow>
            </FormSection>
          </AccordionTab>
        ))}
      </Accordion>

      <ActionRow>
        {onBack && <Button label="Back" icon="pi pi-arrow-left" severity="secondary" onClick={onBack} />}
        {isDirty && <Button label="Save" icon="pi pi-save" loading={loading} className="p-button-success" onClick={handleSubmitData} />}
      </ActionRow>
    </FormWrapper>
  );
};

export default Certifications;
